/**
 * Pure helpers shared across the schedule components. Kept side-effect-free
 * and DOM-free so they can be unit-tested in isolation, and so the
 * CalendarGrid + ActiveStatePanel + EntryList can reuse a single resolver
 * implementation that matches the controller-service `_time_in_window` +
 * `covers` semantics byte-for-byte.
 *
 * Conventions:
 *   * Weekday: 0 = Monday, 6 = Sunday (matches `datetime.weekday()` on the
 *     edge).
 *   * Minute-of-day: 0..1440 inclusive. 1440 == `24:00`, the end-of-day
 *     sentinel the edge accepts as `time_end`.
 *   * `params` mode-specific knobs are stored on the entry; the resolver
 *     does not touch them. Validation lives in the EntryEditor.
 */

import type { ControllerMode, ScheduleDoc, ScheduleEntry } from "@/data/types";

export const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export const DAYS_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;
export const MINUTES_IN_DAY = 1440;

export function parseHm(s: string): number {
  if (s === "24:00") return 1440;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return 0;
  const h = Math.max(0, Math.min(23, Number(m[1])));
  const mm = Math.max(0, Math.min(59, Number(m[2])));
  return h * 60 + mm;
}

export function fmtHm(min: number): string {
  if (min >= 1440) return "24:00";
  const h = Math.floor(Math.max(0, min) / 60);
  const m = Math.max(0, min) % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Snap an arbitrary minute to the nearest `step` (e.g. 15-minute grid). */
export function snapMin(min: number, step: number): number {
  const stepped = Math.round(min / step) * step;
  return Math.max(0, Math.min(MINUTES_IN_DAY, stepped));
}

/** Project epoch-ms into a timezone, returning the weekday + minute. Used
 *  by the "now" indicator + the active-entry resolver — the schedule's
 *  own timezone is authoritative, not the operator's browser locale. */
export function projectInTz(tz: string, atMs: number): {
  weekday: number;
  minute: number;
  hhmm: string;
  ymd: string;
} {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const parts = fmt.formatToParts(new Date(atMs));
    const lookup = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    const map: Record<string, number> = {
      Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
    };
    const weekday = map[lookup("weekday")] ?? 0;
    const h = parseInt(lookup("hour"), 10);
    const m = parseInt(lookup("minute"), 10);
    const minute = (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
    return {
      weekday,
      minute,
      hhmm: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
      ymd: `${lookup("year")}-${lookup("month")}-${lookup("day")}`,
    };
  } catch {
    const d = new Date(atMs);
    return {
      weekday: (d.getDay() + 6) % 7,
      minute: d.getHours() * 60 + d.getMinutes(),
      hhmm: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
      ymd: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    };
  }
}

/** True iff the entry covers the given local (weekday, minute, ymd). */
export function entryCovers(e: ScheduleEntry, weekday: number, minute: number, ymd: string): boolean {
  if (!e.enabled) return false;
  if (!e.days_of_week.includes(weekday)) return false;
  if (e.date_start && ymd < e.date_start) return false;
  if (e.date_end && ymd > e.date_end) return false;
  const s = e.start_min;
  const en = e.end_min;
  if (s === en) return false;
  if (en < s) {
    // wraparound: e.g. 22:00 → 06:00 covers [22:00, 24:00) ∪ [00:00, 06:00)
    return minute >= s || minute < en;
  }
  return s <= minute && minute < en;
}

/** Highest-priority entry covering (weekday, minute, ymd), ties broken
 *  alphabetically by id — matches the edge resolver. */
export function resolveAt(doc: ScheduleDoc, weekday: number, minute: number, ymd: string): ScheduleEntry | null {
  const candidates = doc.entries.filter((e) => entryCovers(e, weekday, minute, ymd));
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => (b.priority - a.priority) || a.id.localeCompare(b.id));
  return candidates[0];
}

/** Contiguous spans of "same entry (or default) over a given day". */
export interface DayBlock {
  startMin: number;
  endMin: number;
  entry: ScheduleEntry | null;   // null == default
}

/**
 * Build the per-day list of contiguous resolved-entry blocks.
 *
 * Implementation: instead of walking every minute (1440 × N entries per
 * day = ~10 k checks per render at N=10, which made interacting with the
 * SchedulePage feel sluggish), we sweep through the relevant entry
 * boundary events in sorted order. For each day there are at most 2×E
 * boundaries (start + end of every covering entry, with wraparound
 * entries contributing the wrap break too), so the algorithm is
 * O(E log E) per day — typically a few dozen comparisons regardless
 * of horizon resolution.
 *
 * At each boundary we keep a "covering set" of active entries; the
 * highest-priority entry (ties broken by id) wins, matching the
 * controller-service resolver. Date constraints (date_start /
 * date_end) require a real date — we use the local-today date in
 * `tz` (passed as `ymd`) as a stand-in for the templated week.
 */
export function blocksForDay(day: number, doc: ScheduleDoc, ymd: string): DayBlock[] {
  // 1. Filter to entries that could cover any part of this day at all.
  const relevant = doc.entries.filter((e) => {
    if (!e.enabled) return false;
    if (!e.days_of_week.includes(day)) return false;
    if (e.date_start && ymd < e.date_start) return false;
    if (e.date_end && ymd > e.date_end) return false;
    if (e.start_min === e.end_min) return false;
    return true;
  });

  // 2. Collect every boundary minute on this day where the active set
  //    can change. For a non-wrap entry [s, e), boundaries are {s, e}.
  //    For a wrap entry [s, 1440) ∪ [0, e), boundaries are {0, e, s, 1440}.
  const boundaries = new Set<number>([0, MINUTES_IN_DAY]);
  for (const e of relevant) {
    const s = e.start_min;
    const en = e.end_min;
    if (en < s) {
      // wraparound on the same day
      boundaries.add(0);
      boundaries.add(en);
      boundaries.add(s);
      boundaries.add(MINUTES_IN_DAY);
    } else {
      boundaries.add(s);
      boundaries.add(en);
    }
  }
  const sortedBoundaries = [...boundaries].sort((a, b) => a - b);

  // 3. Sweep: between each adjacent pair (a, b), pick the highest-
  //    priority entry covering the midpoint. Adjacent intervals with
  //    the same entry id are merged in step 4.
  const intervals: { startMin: number; endMin: number; entry: ScheduleEntry | null }[] = [];
  for (let i = 0; i < sortedBoundaries.length - 1; i++) {
    const a = sortedBoundaries[i];
    const b = sortedBoundaries[i + 1];
    if (b <= a) continue;
    const mid = (a + b) / 2;
    const covering = relevant.filter((e) => {
      const s = e.start_min;
      const en = e.end_min;
      if (en < s) return mid >= s || mid < en;
      return s <= mid && mid < en;
    });
    let winner: ScheduleEntry | null = null;
    if (covering.length) {
      covering.sort((x, y) => (y.priority - x.priority) || x.id.localeCompare(y.id));
      winner = covering[0];
    }
    intervals.push({ startMin: a, endMin: b, entry: winner });
  }

  // 4. Merge adjacent intervals that resolve to the same entry (or both
  //    default). This produces the same shape the old minute-scan emitted,
  //    so the renderer needs no changes.
  const out: DayBlock[] = [];
  for (const it of intervals) {
    if (it.entry === null) continue;   // default is rendered as the gap colour, not a block
    const last = out[out.length - 1];
    if (last && last.entry?.id === it.entry.id && last.endMin === it.startMin) {
      last.endMin = it.endMin;
    } else {
      out.push({ ...it });
    }
  }
  return out;
}

/** Find the next instant in the next 8 days where the resolved entry
 *  changes. Walks day-by-day across `blocksForDay`, so the work is
 *  ~8 × O(E log E) instead of 11 520 × O(E) per probe.
 *
 *  Limitation: the date constraint check inside `blocksForDay` uses a
 *  single `ymd` per day (the local-today date in tz). Date-bounded
 *  entries that cross within the 8-day window therefore activate/deactivate
 *  on the wrong day for the calendar grid. That is fine for the probe —
 *  the controller-service's authoritative probe runs at minute
 *  granularity on the edge — and matches the current calendar's
 *  templating semantics. */
export function nextChangeAfter(
  doc: ScheduleDoc,
  fromMs: number,
): { at: number; mode: ControllerMode } | undefined {
  const tz = doc.timezone;
  const startMin = fromMs - (fromMs % 60_000);
  const startProj = projectInTz(tz, startMin);
  const startActive = resolveAt(doc, startProj.weekday, startProj.minute, startProj.ymd);
  const startId = startActive?.id ?? null;
  // Midnight (in tz) of the day containing `startMin`, expressed as ms.
  // Used as the per-day origin for offsetting blocksForDay times.
  const dayOriginMs = startMin - startProj.minute * 60_000;
  for (let dayOffset = 0; dayOffset < 8; dayOffset++) {
    const weekday = (startProj.weekday + dayOffset) % 7;
    const blocks = blocksForDay(weekday, doc, startProj.ymd);
    // For each block boundary on this day, check whether the active
    // entry changes. The block list gives us start+end pairs at exact
    // boundary minutes — far cheaper than a minute-by-minute walk.
    let lastEnd = 0;
    let lastEntry: ScheduleEntry | null = null;
    for (const b of blocks) {
      if (b.startMin > lastEnd) {
        // Gap before this block → default mode would resolve here.
        const at = dayOriginMs + dayOffset * 86_400_000 + lastEnd * 60_000;
        if (lastEntry !== null && at > startMin) {
          return { at, mode: doc.default.mode };
        }
      }
      const at = dayOriginMs + dayOffset * 86_400_000 + b.startMin * 60_000;
      if (at > startMin) {
        const id = b.entry?.id ?? null;
        if (id !== startId) {
          return { at, mode: b.entry?.mode ?? doc.default.mode };
        }
      }
      lastEnd = b.endMin;
      lastEntry = b.entry;
    }
    // End-of-day fallthrough into default.
    if (lastEnd < MINUTES_IN_DAY && lastEntry !== null) {
      const at = dayOriginMs + dayOffset * 86_400_000 + lastEnd * 60_000;
      if (at > startMin && (startId !== null)) {
        return { at, mode: doc.default.mode };
      }
    }
  }
  return undefined;
}

/** Stable id generator — used by Add Entry. */
export function makeEntryId(): string {
  return `entry-${Math.random().toString(36).slice(2, 7)}-${Date.now().toString(36).slice(-4)}`;
}
